/**
 * PurchaseInvoiceDetailController — page /factures-recues-detail.html
 * (EPIC-017 / 17.6).
 *
 * Hydrate la fiche détail d'une facture reçue depuis
 * `GET /purchase-invoices/{id}` (id en query string `?id=`). Pour le
 * canal `pdp`, le backend rapatrie les champs dynamiques depuis la
 * passerelle PA ; pour le canal `upload`, depuis le JSONB local.
 *
 * Sections rendues :
 *  - en-tête (titre, statut, dates)
 *  - lignes EN16931 (template `tpl-purchase-invoice-line`)
 *  - totaux + détail TVA par taux (template `tpl-purchase-invoice-vat-row`)
 *  - infos fournisseur (sidebar lecture seule)
 *  - timeline events (template `tpl-purchase-invoice-timeline-item`)
 *  - encart archivage commuté selon `origin` (pdp = PA, upload = Notamicro)
 *
 * Formulaire validation : `PATCH /purchase-invoices/{id}` avec
 * `{validationStatus: "validated", expenseCategory, notes}`. Catégorie
 * obligatoire ; avertissement non bloquant si valeur `autre`.
 *
 * Bouton « Rejeter » : stub V1 (toast info, pas de PATCH côté V1 — sera
 * branché ultérieurement).
 *
 * Vocabulaire UI : « PA » / « Plateforme Agréée » uniquement ; jamais
 * « SuperPDP », « PDP », « NotaFacto » ni « OCR ». `pdpGatewayInvoiceId`
 * du payload n'est jamais affiché.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const EXPENSE_CATEGORY_LABELS = {
  achat_marchandises: "Achat de marchandises",
  prestations_externes: "Prestations externes / sous-traitance",
  honoraires_pro_lib: "Honoraires (profession libérale)",
  sous_traitance_indep: "Sous-traitance indépendant",
  commission_apporteur: "Commission / apporteur d'affaires",
  cession_droits_auteur: "Cession de droits d'auteur",
  frais_generaux: "Frais généraux (loyer, énergie, télécoms)",
  transport_deplacement: "Transport / déplacement",
  repas_reception: "Repas / réception",
  formation: "Formation",
  assurances: "Assurances",
  autre: "Autre",
};

const LEGAL_FORM_LABELS = {
  EI: "Entrepreneur individuel",
  micro: "Micro-entreprise",
  liberal: "Profession libérale",
  EURL_transparente: "EURL transparente fiscalement",
  SARL_transparente: "SARL transparente fiscalement",
  autre: "Autre",
};

const STATUS_PILL_MODIFIER = {
  pending: "nm-pill--ochre",
  validated: "nm-pill--blue",
  paid: "nm-pill--olive",
  refused: "nm-pill--red",
  aborted: "",
};

const STATUS_LABELS = {
  pending: "à valider",
  validated: "validée",
  paid: "payée",
  refused: "rejetée",
  aborted: "annulée",
  received: "reçue",
  created: "créée",
  checked: "vérifiée",
};

const EVENT_WHAT = {
  "purchase_invoice.created": "Facture créée",
  "purchase_invoice.validated": "Facture validée",
  "purchase_invoice.paid": "Facture payée",
  "purchase_invoice.aborted": "Facture annulée",
};

const ACTOR_LABELS = {
  system: "système",
  user: "utilisateur",
  customer: "client",
};

const ORIGIN_OVERLINE = {
  pdp: "Facture reçue · reçue via la PA",
  upload: "Facture reçue · upload manuel",
};

export default class PurchaseInvoiceDetailController extends BaseController {
  static targets = [
    "crumbNumber",
    "errorBanner",
    "overline",
    "title",
    "statusPill",
    "statusLabel",
    "headDates",
    "rejectBtn",
    "validateBtn",
    "linesBody",
    "categorySelect",
    "timelineList",
    "supplierName",
    "supplierLines",
    "totalHt",
    "totalTtc",
    "vatBreakdown",
    "paymentBlock",
    "archivePdpBlock",
    "archiveId",
    "archiveExternalBlock",
  ];

  connect() {
    this._invoiceId = this._readIdFromQuery();
    this._submitting = false;
    if (!this._invoiceId) {
      this._showNotFound();
      return;
    }
    this.refresh();
  }

  // ---------------------------------------------------------------------------
  // Hydration
  // ---------------------------------------------------------------------------

  async refresh() {
    try {
      const response = await api.getPurchaseInvoice(this._invoiceId);
      const invoice = response?.data ?? response;
      if (!invoice || typeof invoice !== "object") {
        this._showNotFound();
        return;
      }
      this._invoice = invoice;
      this._render(invoice);
    } catch (error) {
      if (error instanceof APIError && error.status === 404) {
        this._showNotFound();
        return;
      }
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger la facture reçue.";
      this.showError(msg);
    }
  }

  _readIdFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get("id") || "").trim();
    return raw || null;
  }

  _showNotFound() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = false;
    if (this.hasValidateBtnTarget) this.validateBtnTarget.disabled = true;
    if (this.hasRejectBtnTarget) this.rejectBtnTarget.disabled = true;
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  _render(invoice) {
    const ref = this._displayReference(invoice);

    if (this.hasCrumbNumberTarget) this.crumbNumberTarget.textContent = ref;
    if (this.hasTitleTarget) this.titleTarget.textContent = ref;
    if (this.hasOverlineTarget) {
      this.overlineTarget.textContent = ORIGIN_OVERLINE[invoice.origin] ?? "Facture reçue";
    }

    this._renderStatusPill(invoice);
    this._renderHeadDates(invoice);
    this._renderLines(invoice);
    this._renderTotals(invoice);
    this._renderSupplier(invoice);
    this._renderPayment(invoice);
    this._renderArchive(invoice);
    this._renderTimeline(invoice);

    if (this.hasCategorySelectTarget) {
      this.categorySelectTarget.value = invoice.expenseCategory ?? "";
    }

    const isFinal = invoice.validationStatus === "validated"
      || invoice.status === "paid"
      || invoice.status === "refused"
      || invoice.status === "aborted";
    if (this.hasValidateBtnTarget) this.validateBtnTarget.disabled = isFinal;
    if (this.hasRejectBtnTarget) this.rejectBtnTarget.disabled = isFinal;
  }

  _displayReference(invoice) {
    if (invoice.pdpGatewayInvoiceId) return invoice.pdpGatewayInvoiceId;
    if (invoice.supplierName) return invoice.supplierName;
    return "Facture reçue";
  }

  _renderStatusPill(invoice) {
    if (!this.hasStatusPillTarget) return;
    const kind = this._resolvePillKind(invoice);
    const pill = this.statusPillTarget;
    pill.classList.remove(
      "nm-pill--ochre",
      "nm-pill--blue",
      "nm-pill--olive",
      "nm-pill--red",
    );
    const modifier = STATUS_PILL_MODIFIER[kind];
    if (modifier) pill.classList.add(modifier);
    if (this.hasStatusLabelTarget) {
      this.statusLabelTarget.textContent = STATUS_LABELS[kind] ?? kind;
    }
  }

  _resolvePillKind(invoice) {
    if (invoice.status === "paid") return "paid";
    if (invoice.status === "refused" || invoice.status === "aborted") return invoice.status;
    if (invoice.validationStatus === "pending") return "pending";
    if (invoice.validationStatus === "validated") return "validated";
    return invoice.status ?? "pending";
  }

  _renderHeadDates(invoice) {
    if (!this.hasHeadDatesTarget) return;
    const parts = [];
    if (invoice.issuedAt) parts.push(`Reçue le ${this.formatDate(invoice.issuedAt)}`);
    if (invoice.paidAt) parts.push(`payée le ${this.formatDate(invoice.paidAt)}`);
    this.headDatesTarget.textContent = parts.join(" · ");
  }

  _renderLines(invoice) {
    if (!this.hasLinesBodyTarget) return;
    this._clear(this.linesBodyTarget);
    const lines = Array.isArray(invoice.lines) ? invoice.lines : [];
    for (const line of lines) {
      const fragment = this.cloneTemplate("tpl-purchase-invoice-line");
      this.fillSlots(fragment, {
        designation: line.designation ?? line.description ?? "—",
        description: line.detail ?? "",
        quantity: this._formatQuantity(line.quantity),
        unitPriceExclVat: this._formatAmount(line.unitPriceExclVat, true),
        vatRate: this._formatPercent(line.vatRate),
        totalExclVat: this._formatAmount(line.totalExclVat ?? line.amountExclVat, true),
      });
      this.linesBodyTarget.appendChild(fragment);
    }
  }

  _renderTotals(invoice) {
    if (this.hasTotalHtTarget) {
      this.totalHtTarget.textContent = this._formatAmount(invoice.amountExclVat, true);
    }
    if (this.hasTotalTtcTarget) {
      this.totalTtcTarget.textContent = this._formatAmount(invoice.amountInclVat, true);
    }
    this._renderVatBreakdown(invoice);
  }

  _renderVatBreakdown(invoice) {
    if (!this.hasVatBreakdownTarget) return;
    const breakdown = invoice.vatBreakdown && typeof invoice.vatBreakdown === "object"
      ? invoice.vatBreakdown
      : {};
    const entries = Object.entries(breakdown);
    const parent = this.vatBreakdownTarget.parentNode;
    if (!parent) return;

    while (this.vatBreakdownTarget.nextSibling
      && this.vatBreakdownTarget.nextSibling.dataset
      && this.vatBreakdownTarget.nextSibling.dataset.vatBreakdownGenerated === "true") {
      parent.removeChild(this.vatBreakdownTarget.nextSibling);
    }

    if (entries.length === 0) {
      this.vatBreakdownTarget.hidden = true;
      return;
    }

    this.vatBreakdownTarget.hidden = true;
    let lastInserted = this.vatBreakdownTarget;
    for (const [rate, amount] of entries) {
      const fragment = this.cloneTemplate("tpl-purchase-invoice-vat-row");
      this.fillSlots(fragment, {
        label: `TVA ${this._formatPercent(rate)}`,
        value: this._formatAmount(amount, true),
      });
      const row = fragment.firstElementChild;
      if (row instanceof HTMLElement) row.dataset.vatBreakdownGenerated = "true";
      parent.insertBefore(fragment, lastInserted.nextSibling);
      lastInserted = lastInserted.nextElementSibling ?? lastInserted;
    }
  }

  _renderSupplier(invoice) {
    if (this.hasSupplierNameTarget) {
      this.supplierNameTarget.textContent = invoice.supplierName || "—";
    }
    if (!this.hasSupplierLinesTarget) return;
    const target = this.supplierLinesTarget;
    this._clear(target);
    const lines = [];
    if (invoice.supplierSiret) {
      const s = document.createElement("span");
      s.className = "is-mono";
      s.textContent = `SIRET ${invoice.supplierSiret}`;
      lines.push(s);
    }
    const form = LEGAL_FORM_LABELS[invoice.supplierLegalForm];
    if (form) {
      const f = document.createElement("span");
      f.textContent = form;
      lines.push(f);
    }
    if (invoice.supplierCountry) {
      const c = document.createElement("span");
      c.textContent = invoice.supplierCountry;
      lines.push(c);
    }
    if (lines.length === 0) {
      target.textContent = "—";
      return;
    }
    lines.forEach((node, idx) => {
      if (idx > 0) target.appendChild(document.createElement("br"));
      target.appendChild(node);
    });
  }

  _renderPayment(invoice) {
    if (!this.hasPaymentBlockTarget) return;
    const linesContainer = this.paymentBlockTarget.querySelector(".nm-infoblock__lines");
    if (!linesContainer) return;
    this._clear(linesContainer);
    const dueAt = invoice.dueAt || invoice.paidAt;
    if (dueAt) {
      const label = invoice.paidAt ? "Réglée le" : "Échéance";
      const strong = document.createElement("strong");
      strong.className = "text-default";
      strong.style.fontWeight = "500";
      const t = document.createElement("time");
      t.setAttribute("datetime", dueAt);
      t.textContent = this.formatDate(dueAt);
      strong.appendChild(t);
      linesContainer.append(`${label} : `, strong);
    } else {
      linesContainer.textContent = "À régler dès réception.";
    }
  }

  _renderArchive(invoice) {
    const isPdp = invoice.origin === "pdp";
    if (this.hasArchivePdpBlockTarget) this.archivePdpBlockTarget.hidden = !isPdp;
    if (this.hasArchiveExternalBlockTarget) {
      this.archiveExternalBlockTarget.hidden = isPdp;
    }
    if (isPdp && this.hasArchiveIdTarget) {
      this.archiveIdTarget.textContent = invoice.pdpGatewayInvoiceId
        ? `ID archivage : ${invoice.pdpGatewayInvoiceId}`
        : "";
    }
  }

  _renderTimeline(invoice) {
    if (!this.hasTimelineListTarget) return;
    this._clear(this.timelineListTarget);
    const events = Array.isArray(invoice.events) ? invoice.events : [];
    if (events.length === 0) {
      const empty = document.createElement("li");
      empty.className = "is-muted fs-13";
      empty.textContent = "Aucun événement enregistré.";
      this.timelineListTarget.appendChild(empty);
      return;
    }
    for (const event of events) {
      const fragment = this.cloneTemplate("tpl-purchase-invoice-timeline-item");
      const what = EVENT_WHAT[event.event] ?? event.event;
      const who = ACTOR_LABELS[event.actor] ?? event.actor ?? "";
      this.fillSlots(fragment, {
        iso: event.happenedAt ?? "",
        when: event.happenedAt ? this._formatTimelineWhen(event.happenedAt) : "",
        what,
        who: who ? `par ${who}` : "",
      });
      this.timelineListTarget.appendChild(fragment);
    }
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  onCategoryChange() {
    if (!this._invoice) return;
    if (this.categorySelectTarget.value === "autre") {
      this.showError("Attention : la catégorie « Autre » nécessitera une révision lors de la déclaration DAS2.");
    }
  }

  downloadPdf(event) {
    if (event) event.preventDefault();
    if (!this._invoiceId) return;
    const url = `/purchase-invoices/${encodeURIComponent(this._invoiceId)}/pdf?download=1`;
    window.open(url, "_blank", "noopener");
  }

  async reject(event) {
    if (event) event.preventDefault();
    const ok = await this.confirm("Rejeter cette facture reçue ?");
    if (!ok) return;
    this.showSuccess("Le rejet d'une facture reçue sera disponible dans une prochaine version.");
  }

  async submit(event) {
    if (event) event.preventDefault();
    if (!this._invoiceId || this._submitting) return;
    if (!this.hasCategorySelectTarget) return;

    const category = this.categorySelectTarget.value;
    if (!category) {
      this.showError("Sélectionnez une catégorie de dépense avant de valider.");
      this.categorySelectTarget.focus();
      return;
    }

    this._submitting = true;
    if (this.hasValidateBtnTarget) this.validateBtnTarget.disabled = true;

    try {
      const response = await api.patchPurchaseInvoice(this._invoiceId, {
        validationStatus: "validated",
        expenseCategory: category,
      });
      const updated = response?.data ?? response;
      if (updated && typeof updated === "object") {
        this._invoice = updated;
        this._render(updated);
      }
      this.showSuccess("Facture validée.");
    } catch (error) {
      if (this.hasValidateBtnTarget) this.validateBtnTarget.disabled = false;
      const msg = error instanceof APIError && error.message
        ? error.message
        : "La validation a échoué. Vérifiez la catégorie puis réessayez.";
      this.showError(msg);
    } finally {
      this._submitting = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  _clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  _formatAmount(raw, withSymbol = false) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    const txt = num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return withSymbol ? `${txt} €` : txt;
  }

  _formatQuantity(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    return num.toLocaleString("fr-FR", { maximumFractionDigits: 3 });
  }

  _formatPercent(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return String(raw);
    return `${num.toLocaleString("fr-FR", { maximumFractionDigits: 2 })} %`;
  }

  _formatTimelineWhen(iso) {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
}
